import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ThumbsUp, ThumbsDown, Send, Trash2, CornerDownRight, ChevronLeft, ChevronRight, RefreshCw, Loader2, Upload, X, GripVertical, Pencil, Check } from 'lucide-react';
import api from '../utils/api';
import AnalysisRenderer from '../components/AnalysisRenderer';

const STATUS_OPTIONS = [
  { value: 'shared',    label: '공유됨' },
  { value: 'reviewing', label: '검토중' },
  { value: 'adopted',   label: '채택' },
  { value: 'pending',   label: '보류' },
];

const STATUS_COLORS = {
  shared:    'text-blue-400 border-blue-400/30',
  reviewing: 'text-yellow-400 border-yellow-400/30',
  adopted:   'text-green-400 border-green-400/30',
  pending:   'text-red-400 border-red-400/30',
  draft:     'text-gray-400 border-gray-700',
};

export default function IdeaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const me = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isSuperadmin = me.role === 'superadmin';

  const [idea, setIdea] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [comment, setComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [voting, setVoting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', content: '' });
  const [saveStatus, setSaveStatus] = useState(null);
  const autoSaveTimer = useRef(null);
  const editingStarted = useRef(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [imageProgress, setImageProgress] = useState({ current: 0, total: 3, label: '' });
  const [regeneratingImageId, setRegeneratingImageId] = useState(null);
  const [regenHintFor, setRegenHintFor] = useState(null);
  const [regenHint, setRegenHint] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [refImageFor, setRefImageFor] = useState(null); // ideaId when uploading ref from detail
  const [refUploading, setRefUploading] = useState(false);
  const dragImgRef = useRef(null);
  const replyRef = useRef(null);
  const regenHintRef = useRef(null);
  const uploadFileRef = useRef(null);
  const refFileRef = useRef(null);

  const load = async () => {
    const r = await api.get(`/ideas/${id}`);
    setIdea(r.data);
  };

  // 제목·내용 자동 저장 (1.5초 디바운스)
  useEffect(() => {
    if (!editing || !editingStarted.current) return;
    clearTimeout(autoSaveTimer.current);
    setSaveStatus(null);
    autoSaveTimer.current = setTimeout(async () => {
      if (!editForm.title.trim()) return;
      setSaveStatus('saving');
      try {
        const contentField = idea?.type === 'research' ? 'researchReport' : 'analysis';
        await api.put(`/ideas/${id}`, { title: editForm.title, [contentField]: editForm.content });
        setIdea(prev => ({ ...prev, title: editForm.title, [contentField]: editForm.content }));
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } catch {
        setSaveStatus(null);
      }
    }, 1500);
    return () => clearTimeout(autoSaveTimer.current);
  }, [editForm.title, editForm.content]);

  const startEdit = () => {
    editingStarted.current = false;
    const content = idea?.type === 'research' ? (idea.researchReport || '') : (idea.analysis || '');
    setEditForm({ title: idea?.title || '', content });
    setEditing(true);
    setTimeout(() => { editingStarted.current = true; }, 100);
  };

  const cancelEdit = () => {
    editingStarted.current = false;
    clearTimeout(autoSaveTimer.current);
    setSaveStatus(null);
    setEditing(false);
  };

  const regenerateImages = async () => {
    setGeneratingImages(true);
    setImageProgress({ current: 0, total: 3, label: '시작 중...' });
    const token = localStorage.getItem('mfg_token');
    const response = await fetch(`/api/ideas/${id}/generate-images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n'); buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.step === 'prompt') setImageProgress(p => ({ ...p, label: data.message }));
            if (data.step === 'generating') setImageProgress({ current: data.current - 1, total: data.total, label: `${data.label} 생성 중... (${data.current}/${data.total})` });
            if (data.step === 'done') { setImageProgress(p => ({ ...p, current: p.total })); load(); }
            if (data.step === 'error') alert(data.message);
          } catch {}
        }
      }
    } finally {
      setGeneratingImages(false);
    }
  };

  const regenerateSingleImage = async (imageId, hint) => {
    setRegenHintFor(null);
    setRegenHint('');
    setRegeneratingImageId(imageId);
    try {
      const r = await api.post(`/ideas/${id}/images/${imageId}/regenerate`, { hint: hint || undefined });
      setIdea(prev => ({
        ...prev,
        images: prev.images.map(img => img.id === imageId ? { ...img, url: r.data.url } : img),
      }));
    } catch (e) {
      alert('재생성 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setRegeneratingImageId(null);
    }
  };

  const uploadImage = async (file) => {
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const r = await api.post(`/ideas/${id}/images/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setIdea(prev => ({ ...prev, images: [...(prev.images || []), r.data] }));
    } catch (e) {
      alert('업로드 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!confirm('이 이미지를 삭제하시겠습니까?')) return;
    await api.delete(`/ideas/${id}/images/${imageId}`);
    setIdea(prev => {
      const images = prev.images.filter(img => img.id !== imageId);
      return { ...prev, images };
    });
    setImgIdx(i => Math.min(i, Math.max(0, idea.images.length - 2)));
  };

  const uploadReferenceImage = async (file) => {
    setRefUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const r = await api.post(`/ideas/${id}/reference-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setIdea(prev => ({ ...prev, referenceImageUrl: r.data.url }));
    } catch (e) {
      alert('참고 이미지 업로드 실패');
    } finally {
      setRefUploading(false);
    }
  };

  const removeReferenceImage = async () => {
    await api.delete(`/ideas/${id}/reference-image`);
    setIdea(prev => ({ ...prev, referenceImageUrl: null }));
  };

  const reorderImages = async (newImages) => {
    setIdea(prev => ({ ...prev, images: newImages }));
    await api.put(`/ideas/${id}/images/reorder`, {
      orders: newImages.map((img, i) => ({ id: img.id, order: i })),
    });
  };

  // 드래그-앤-드롭 (썸네일)
  const onThumbDragStart = (i) => { dragImgRef.current = i; };
  const onThumbDrop = (i) => {
    const from = dragImgRef.current;
    if (from === null || from === i) return;
    dragImgRef.current = null;
    const imgs = [...(idea?.images || [])];
    const [moved] = imgs.splice(from, 1);
    imgs.splice(i, 0, moved);
    setImgIdx(i);
    reorderImages(imgs);
  };

  useEffect(() => { load(); }, [id]);

  if (!idea) return <div className="text-gray-600 text-sm">불러오는 중...</div>;

  const images = idea.images || [];
  const votes = idea.votes || [];
  const up = votes.filter(v => v.type === 'up').length;
  const down = votes.filter(v => v.type === 'down').length;
  const myVote = votes.find(v => v.adminId === me.id)?.type;
  const content = idea.type === 'research' ? idea.researchReport : idea.analysis;
  const st = STATUS_COLORS[idea.status] || STATUS_COLORS.draft;

  const vote = async (type) => {
    if (voting) return;
    setVoting(true);
    try {
      const r = await api.post(`/ideas/${id}/vote`, { type });
      setIdea(prev => ({ ...prev, votes: r.data }));
    } catch (e) {
      alert('투표 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setVoting(false);
    }
  };

  const changeStatus = async (status) => {
    await api.put(`/ideas/${id}/status`, { status });
    setIdea(prev => ({ ...prev, status }));
  };

  const deleteIdea = async () => {
    if (!confirm('이 아이디어를 삭제하시겠습니까?')) return;
    await api.delete(`/ideas/${id}`);
    navigate('/idea-archive');
  };

  const submitComment = async () => {
    if (!comment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/ideas/${id}/comments`, { content: comment.trim() });
      setComment('');
      load();
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentId) => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/ideas/${id}/comments`, { content: replyText.trim(), parentId });
      setReplyText(''); setReplyTo(null);
      load();
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (commentId) => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/ideas/${id}/comments/${commentId}`);
    load();
  };

  const fmt = d => new Date(d).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <button onClick={() => navigate('/idea-archive')}
          className="flex items-center gap-1.5 text-gray-500 hover:text-white text-sm flex-shrink-0">
          <ArrowLeft size={15} /> 아카이브
        </button>
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {isSuperadmin ? (
            <select value={idea.status} onChange={e => changeStatus(e.target.value)}
              className={`bg-transparent border rounded-lg px-2 py-1 text-xs focus:outline-none ${st}`}>
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value} className="bg-gray-900 text-white">{o.label}</option>
              ))}
            </select>
          ) : (
            <span className={`text-xs border rounded-full px-2 py-0.5 ${st}`}>
              {STATUS_OPTIONS.find(o => o.value === idea.status)?.label || idea.status}
            </span>
          )}
          {(idea.creator?.id === me.id || isSuperadmin) && !editing && (
            <button onClick={startEdit} className="p-1.5 text-gray-600 hover:text-gray-300 rounded-lg hover:bg-gray-800" title="수정">
              <Pencil size={14} />
            </button>
          )}
          {editing && (
            <>
              {saveStatus === 'saving' && <span className="text-gray-600 text-xs">저장 중...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-xs">저장됨</span>}
              <button onClick={cancelEdit} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
                <X size={14} />
              </button>
            </>
          )}
          {(idea.creator?.id === me.id || isSuperadmin) && !editing && (
            <button onClick={deleteIdea} className="p-1.5 text-gray-600 hover:text-red-400 rounded-lg hover:bg-gray-800">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {/* 제목 + 메타 카드 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          {/* 이미지 갤러리 */}
          {/* 이미지 컨트롤 바 */}
          {(idea.creator?.id === me.id || isSuperadmin) && (
            <div className="px-4 pt-3 pb-1 flex items-center justify-between gap-2">
              {/* 참고 이미지 */}
              <div className="flex items-center gap-2">
                {idea.referenceImageUrl ? (
                  <div className="relative group">
                    <img src={idea.referenceImageUrl} alt="참고" className="w-8 h-8 rounded object-cover border border-gray-700" title="참고 이미지" />
                    <button onClick={removeReferenceImage}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full items-center justify-center text-white hidden group-hover:flex">
                      <X size={8} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => refFileRef.current?.click()} disabled={refUploading}
                    className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-400 transition-colors" title="참고 이미지 업로드">
                    {refUploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                    <span>참고</span>
                  </button>
                )}
                <input ref={refFileRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadReferenceImage(f); e.target.value = ''; }} />
              </div>
              {/* AI 이미지 생성/재생성 */}
              {idea.source === 'ai' && (
                generatingImages ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Loader2 size={11} className="animate-spin" />
                    {imageProgress.label}
                    <div className="w-20 bg-gray-800 rounded-full h-1">
                      <div className="h-full bg-brand rounded-full transition-all"
                        style={{ width: `${Math.max(5, (imageProgress.current / imageProgress.total) * 100)}%` }} />
                    </div>
                  </div>
                ) : (
                  <button onClick={regenerateImages}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white border border-gray-800 hover:border-gray-600 rounded-lg px-2.5 py-1.5 transition-colors">
                    <RefreshCw size={11} />
                    {images.length === 0 ? 'AI 이미지 생성' : 'AI 전체 재생성'}
                  </button>
                )
              )}
            </div>
          )}

          {/* 메인 이미지 뷰어 */}
          {images.length > 0 && (
            <div className="relative bg-gray-950">
              {regeneratingImageId === images[imgIdx]?.id ? (
                <div className="w-full h-64 flex items-center justify-center">
                  <Loader2 size={24} className="animate-spin text-gray-500" />
                </div>
              ) : (
                <img src={images[imgIdx]?.url} alt="컨셉" className="w-full max-h-[480px] object-contain mx-auto block" />
              )}
              {/* AI 태그 */}
              {images[imgIdx]?.source === 'ai' && (
                <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 bg-brand/80 text-white rounded font-medium">AI</span>
              )}
              {/* 개별 재생성 (AI 이미지만) */}
              {idea.source === 'ai' && images[imgIdx]?.source === 'ai' && (idea.creator?.id === me.id || isSuperadmin) && (
                <div className="absolute top-2 right-2">
                  {regenHintFor === images[imgIdx]?.id ? (
                    <div className="flex items-center gap-1 bg-black/80 rounded-lg px-2 py-1.5">
                      <input ref={regenHintRef} value={regenHint} onChange={e => setRegenHint(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') regenerateSingleImage(images[imgIdx]?.id, regenHint);
                          if (e.key === 'Escape') { setRegenHintFor(null); setRegenHint(''); }
                        }}
                        placeholder="힌트 (선택사항)"
                        className="bg-transparent text-white text-xs placeholder-gray-500 outline-none w-28" />
                      <button onClick={() => regenerateSingleImage(images[imgIdx]?.id, regenHint)} className="text-brand text-xs">생성</button>
                      <button onClick={() => { setRegenHintFor(null); setRegenHint(''); }} className="text-gray-500 text-xs">✕</button>
                    </div>
                  ) : (
                    <button onClick={() => { setRegenHintFor(images[imgIdx]?.id); setTimeout(() => regenHintRef.current?.focus(), 50); }}
                      disabled={regeneratingImageId !== null}
                      className="w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white disabled:opacity-40 transition-colors">
                      <RefreshCw size={12} className={regeneratingImageId === images[imgIdx]?.id ? 'animate-spin' : ''} />
                    </button>
                  )}
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={() => setImgIdx(i => (i - 1 + images.length) % images.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white">
                    <ChevronLeft size={16} />
                  </button>
                  <button onClick={() => setImgIdx(i => (i + 1) % images.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white">
                    <ChevronRight size={16} />
                  </button>
                </>
              )}
            </div>
          )}

          {/* 썸네일 스트립 — 드래그 순서 변경 + 업로드 */}
          {(images.length > 0 || (idea.creator?.id === me.id || isSuperadmin)) && (
            <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto">
              {images.map((img, i) => (
                <div key={img.id}
                  draggable
                  onDragStart={() => onThumbDragStart(i)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => onThumbDrop(i)}
                  className={`group relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 cursor-grab transition-all ${i === imgIdx ? 'border-brand' : 'border-gray-700 opacity-60 hover:opacity-100'}`}
                  onClick={() => setImgIdx(i)}>
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                  {img.source === 'ai' && (
                    <span className="absolute bottom-0 left-0 right-0 text-center text-[8px] bg-brand/70 text-white leading-4">AI</span>
                  )}
                  {(idea.creator?.id === me.id || isSuperadmin) && (
                    <button onClick={e => { e.stopPropagation(); deleteImage(img.id); }}
                      className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 rounded-full hidden group-hover:flex items-center justify-center text-white hover:bg-red-500/80">
                      <X size={8} />
                    </button>
                  )}
                </div>
              ))}
              {/* 이미지 추가 업로드 버튼 */}
              {(idea.creator?.id === me.id || isSuperadmin) && (
                <button onClick={() => uploadFileRef.current?.click()} disabled={uploadingImage}
                  className="flex-shrink-0 w-14 h-14 rounded-lg border-2 border-dashed border-gray-700 flex items-center justify-center text-gray-600 hover:text-gray-400 hover:border-gray-500 transition-colors">
                  {uploadingImage ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                </button>
              )}
              <input ref={uploadFileRef} type="file" accept="image/*" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ''; }} />
            </div>
          )}

          <div className="px-4 py-4">
            {editing ? (
              <input
                value={editForm.title}
                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                className="w-full bg-transparent text-white font-bold text-base focus:outline-none border-b border-gray-700 pb-1 mb-1"
                placeholder="제목"
              />
            ) : (
              <h1 className="text-white font-bold text-base mb-1">{idea.title || '제목 없음'}</h1>
            )}
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-[11px]">
                {idea.creator?.name || idea.creator?.username} · {fmt(idea.createdAt)}
              </span>
              <div className="flex gap-1">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${idea.source === 'manual' ? 'bg-gray-800 text-gray-400' : 'bg-brand/10 text-brand'}`}>
                  {idea.source === 'manual' ? '직접' : 'AI'}
                </span>
              </div>
            </div>
            {/* 태그 */}
            {idea.tags && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {idea.tags.split(',').filter(t => t.trim()).map(t => (
                  <span key={t} className="text-[10px] px-2 py-0.5 bg-gray-800 text-gray-400 rounded-full">{t.trim()}</span>
                ))}
              </div>
            )}
          </div>

          {/* 투표 */}
          <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-800">
            <button onClick={() => vote('up')} disabled={voting}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors disabled:opacity-50 ${myVote === 'up' ? 'bg-green-900/30 border-green-700 text-green-400' : 'border-gray-800 text-gray-500 hover:text-green-400'}`}>
              <ThumbsUp size={14} /> {up}
            </button>
            <button onClick={() => vote('down')} disabled={voting}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors disabled:opacity-50 ${myVote === 'down' ? 'bg-red-900/30 border-red-700 text-red-400' : 'border-gray-800 text-gray-500 hover:text-red-400'}`}>
              <ThumbsDown size={14} /> {down}
            </button>
          </div>
        </div>

        {/* 본문 */}
        {(content || editing) && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            {editing ? (
              <textarea
                value={editForm.content}
                onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))}
                className="w-full bg-transparent text-gray-300 text-sm focus:outline-none resize-none leading-relaxed"
                rows={Math.max(10, editForm.content.split('\n').length + 2)}
                placeholder="내용을 입력하세요..."
              />
            ) : idea.source === 'manual' ? (
              <div className="prose prose-invert prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: content }} />
            ) : (
              <AnalysisRenderer text={content} />
            )}
          </div>
        )}

        {/* 의견 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800">
            <h3 className="text-white font-semibold text-sm">의견 {idea.comments?.length > 0 ? `(${idea.comments.length})` : ''}</h3>
          </div>

          <div className="px-4 py-3 space-y-4">
            {(idea.comments || []).map(c => (
              <div key={c.id}>
                <div className="group flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-300 font-medium flex-shrink-0">
                    {(c.author?.name || c.author?.username || '?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-white text-xs font-semibold">{c.author?.name || c.author?.username}</span>
                      <span className="text-gray-600 text-[10px]">{fmt(c.createdAt)}</span>
                    </div>
                    <p className="text-gray-300 text-sm mt-0.5 whitespace-pre-wrap">{c.content}</p>
                    <div className="flex gap-3 mt-1">
                      <button onClick={() => { setReplyTo(c.id); setTimeout(() => replyRef.current?.focus(), 50); }}
                        className="text-gray-600 hover:text-gray-400 text-[11px]">답글</button>
                      {(c.authorId === me.id || isSuperadmin) && (
                        <button onClick={() => deleteComment(c.id)}
                          className={`text-[11px] hover:text-red-400 transition-colors ${isSuperadmin ? 'text-gray-500' : 'text-gray-700 opacity-0 group-hover:opacity-100'}`}>삭제</button>
                      )}
                    </div>
                  </div>
                </div>
                {(c.replies || []).map(r => (
                  <div key={r.id} className="group flex gap-2 mt-2 ml-9">
                    <CornerDownRight size={11} className="text-gray-700 flex-shrink-0 mt-1" />
                    <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[10px] text-gray-300 font-medium flex-shrink-0">
                      {(r.author?.name || r.author?.username || '?')[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-white text-xs font-semibold">{r.author?.name || r.author?.username}</span>
                        <span className="text-gray-600 text-[10px]">{fmt(r.createdAt)}</span>
                      </div>
                      <p className="text-gray-300 text-sm mt-0.5">{r.content}</p>
                      {(r.authorId === me.id || isSuperadmin) && (
                        <button onClick={() => deleteComment(r.id)}
                          className={`text-[11px] hover:text-red-400 transition-colors mt-0.5 ${isSuperadmin ? 'text-gray-500' : 'text-gray-700 opacity-0 group-hover:opacity-100'}`}>삭제</button>
                      )}
                    </div>
                  </div>
                ))}
                {replyTo === c.id && (
                  <div className="flex gap-2 mt-2 ml-9">
                    <input ref={replyRef} value={replyText} onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitReply(c.id); } }}
                      placeholder="답글..."
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand" />
                    <button onClick={() => submitReply(c.id)} className="p-1.5 text-brand"><Send size={14} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 댓글 입력 */}
          <div className="flex items-center gap-2.5 px-4 py-3 border-t border-gray-800">
            <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-300 font-medium flex-shrink-0">
              {(me.name || me.username || '?')[0]}
            </div>
            <input value={comment} onChange={e => setComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
              placeholder="의견을 남겨주세요..."
              className="flex-1 bg-gray-800/60 border border-gray-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand/50 placeholder-gray-600" />
            <button onClick={submitComment} disabled={!comment.trim()} className="p-1.5 text-brand disabled:opacity-30">
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
